import { useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import GradesPDF from "../assets/grades.pdf";
import DL1 from "../assets/DL1.jpg";
import DL2 from "../assets/DL2.jpg";
import TeachingVideo from "../assets/T2.mp4";

// Placeholder Images
const TeachingImg = "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=2070&auto=format&fit=crop";
const GradesImg = "https://images.unsplash.com/photo-1550592704-6c76defa9985?q=80&w=2070&auto=format&fit=crop";

// ... existing components ...



const PolaroidPile = () => {
    const [activeIndex, setActiveIndex] = useState(0);

    const items = [
        { type: "image", src: DL1, caption: "Dean's List #1: init commit", rotate: -2 },
        { type: "image", src: DL2, caption: "Dean's List #2: feat: pattern", rotate: 3 },
        { type: "missing", caption: "Dean's List #3: chore: album", rotate: -4, color: "bg-gray-800" },
        { type: "missing", caption: "Dean's List #4: chore: attend", rotate: 1, color: "bg-[#ff5c7a]/20" },
    ];

    const handleNext = () => {
        setActiveIndex((prev) => (prev + 1) % items.length);
    };

    return (
        <div className="relative w-64 h-80" onClick={handleNext}>
            {items.map((item, index) => {
                // Calculate position in stack relative to active index
                const offset = (index - activeIndex + items.length) % items.length;
                const isTop = offset === 0;

                // Visual styling based on stack position
                const zIndex = items.length - offset;
                const scale = isTop ? 1 : 0.9 + (0.1 * (items.length - offset) / items.length);
                const rotate = isTop ? 0 : item.rotate; // Straighten top card on hover/focus maybe? Nah, playful is better.
                const opacity = offset > 2 ? 0 : 1; // Hide items deep in stack
                const yOffset = isTop ? 0 : offset * -10; // Stack vertically a bit

                return (
                    <motion.div
                        key={index}
                        animate={{
                            zIndex,
                            scale,
                            rotate: isTop ? 0 : item.rotate,
                            y: yOffset,
                            opacity
                        }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                        className={`absolute inset-0 p-3 bg-white pb-12 shadow-2xl cursor-pointer transition-transform duration-300 hover:rotate-2 border border-white/20 ${isTop ? 'border-4 border-[#ff5c7a]/50' : ''}`}
                        style={{
                            transformOrigin: "bottom center",
                        }}
                    >
                        {item.type === "image" ? (
                            <div className="w-full h-full overflow-hidden bg-gray-200">
                                <img src={item.src} alt="Dean's List" className="w-full h-full object-cover filter grayscale-[20%]" />
                            </div>
                        ) : (
                            <div className={`w-full h-full flex flex-col items-center justify-center p-4 text-center ${item.color}`}>
                                <span className="text-4xl mb-4 opacity-50">?</span>
                                <span className="font-mono text-xs uppercase text-black/50 tracking-widest bg-white/10 p-1">lore incoming</span>
                            </div>
                        )}
                        <div className="absolute bottom-3 left-0 right-0 text-center">
                            <p className="font-handwriting text-black text-sm font-bold opacity-80 rotate-[-1deg]">{item.caption}</p>
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
};

interface Achievement {
    id: string;
    title: string;
    metric: string;
    description: string;
    image: string;
    layout: "stat-card" | "hero-card" | "split-card";
    gallery?: string[];
}

const achievements: Achievement[] = [
    {
        id: "gpa",
        title: "The 4.0 GPA Streak",
        metric: "4.0 GPA",
        description: "By some strange coincidence (or maybe just stubborn consistency), I’ve managed to bag a 4.0 GPA in 3 out of the last 4 semesters. And the secret? I didn’t chase it, It just happened once and I had to keep it that way ;)",
        image: GradesImg,
        layout: "stat-card"
    },
    {
        id: "deans-list",
        title: "Dean's List x4",
        metric: "Awarded",
        description: "There is a different kind of satisfaction in knowing you didn't just survive the semester, you owned it. The award is great but it’s really a bookmark in a much longer story about mastery ;)",
        image: DL1, // Fallback for list, but component uses custom gallery
        layout: "hero-card"
    },
    {
        id: "teaching",
        title: "Kuppi",
        metric: "Mentorship",
        description: "Einstein said you don't understand something unless you can explain it to a six-year-old, so I use my batchmates as test subjects. Now, I'm not saying my friends are 6-year-olds (OBVIOUSLY) but the logic holds up. If I can’t strip away the noise and make it click instantly, then I’m just bluffing. And I don’t bluff 🗿",
        image: TeachingImg,
        layout: "split-card"
    }
];

export default function AcademicsSection() {
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"]
    });

    const y = useTransform(scrollYProgress, [0, 1], [100, -100]);

    return (
        <section id="academics" ref={containerRef} className="relative min-h-screen bg-[#0c0c0c] py-32 border-t border-white/10">
            <div className="max-w-[1900px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">

                {/* Sticky Header - Left Column */}
                <div className="lg:col-span-4 relative">
                    <div className="sticky top-32 space-y-8">
                        <span className="block text-xs font-mono text-[#ff5c7a] tracking-widest uppercase">
                            03 / The Archive
                        </span>
                        <h2 className="text-5xl md:text-8xl font-display font-bold text-white leading-[0.9] tracking-tighter">
                            ACA<br className="hidden md:inline" />DEM<br className="hidden md:inline" />ICS
                        </h2>
                        <div className="pt-4 border-l-2 border-[#ff5c7a] pl-4">
                            <blockquote className="text-white/70 italic text-sm leading-relaxed">
                                "The whole purpose of education is to turn mirrors into windows."
                            </blockquote>
                            <cite className="block mt-2 text-[#ff5c7a] text-xs font-mono uppercase tracking-widest not-italic">
                                — Sydney J. Harris
                            </cite>
                        </div>

                        {/* Decorative decorative line */}
                        <div className="w-12 h-1 bg-white/20 mt-12" />
                    </div>
                </div>

                {/* Content Stream - Right Column */}
                <div className="lg:col-span-8 space-y-32 pt-12 lg:pt-0">
                    {achievements.map((item, index) => (
                        <div key={item.id} className="group relative">
                            {/* Card Index */}
                            <span className="absolute -left-12 top-0 text-white/10 font-mono text-6xl -rotate-90 origin-bottom-right hidden lg:block">
                                0{index + 1}
                            </span>

                            {/* Layout Logic */}
                            {item.layout === "stat-card" && (
                                <motion.div
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-10%" }}
                                    transition={{ duration: 0.8 }}
                                    className="bg-white/5 border border-white/10 p-8 md:p-12 relative overflow-hidden"
                                >
                                    <div className="relative z-10 grid md:grid-cols-[30%_70%] gap-8 items-center">
                                        <div>
                                            <h3 className="text-[8rem] leading-none font-display font-bold text-transparent bg-clip-text bg-gradient-to-b from-white to-white/40">
                                                4.0
                                            </h3>
                                        </div>
                                        <div className="space-y-6">
                                            <h4 className="text-3xl font-display text-white">{item.title}</h4>
                                            <p className="text-white/70 leading-relaxed text-lg">
                                                {item.description}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="relative z-10 mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <a
                                            href={GradesPDF}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-8 py-4 border border-white/20 bg-white/5 hover:bg-white hover:text-black transition-all duration-300 uppercase tracking-widest text-sm font-mono flex items-center justify-between group/btn"
                                        >
                                            <span>View Grades</span>
                                            <span className="group-hover/btn:-rotate-45 transition-transform duration-300">↗</span>
                                        </a>
                                        {/* <button className="px-8 py-4 border border-white/20 bg-[#ff5c7a]/10 hover:bg-[#ff5c7a] hover:text-black transition-all duration-300 uppercase tracking-widest text-sm font-mono flex items-center justify-between group/article">
                                            <span className="truncate mr-4">Read "How to get a 4.0"</span>
                                            <span className="group-hover/article:translate-x-1 transition-transform">→</span>
                                        </button> */}
                                    </div>

                                    {/* Abstract BG element */}
                                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-b from-white/5 to-transparent blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />
                                </motion.div>
                            )}

                            {item.layout === "hero-card" && (
                                <div className="grid lg:grid-cols-2 gap-8 md:gap-12 bg-white/5 border border-white/10 p-8 md:p-12">
                                    {/* Left Content */}
                                    <div className="flex flex-col justify-center space-y-8 order-2 lg:order-1">
                                        <div>
                                            <span className="text-[#ff5c7a] font-mono text-sm uppercase tracking-wider mb-2 block">{item.metric}</span>
                                            <h3 className="text-4xl md:text-5xl font-display text-white mb-4">{item.title}</h3>
                                            <p className="text-white/80 text-lg leading-relaxed">{item.description}</p>
                                        </div>
                                    </div>

                                    {/* Right: Interactive Polaroid Pile */}
                                    <div className="relative h-[400px] flex items-center justify-center order-1 lg:order-2 perspective-1000 group/pile cursor-pointer">
                                        <PolaroidPile />
                                    </div>
                                </div>
                            )}

                            {item.layout === "split-card" && (
                                <motion.div
                                    initial={{ opacity: 0, x: 50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: "-10%" }}
                                    transition={{ duration: 0.8 }}
                                    className="grid md:grid-cols-2 gap-0 border border-white/10 bg-white/5"
                                >
                                    {/* Left: Video Container (The "Hands-on Screen") */}
                                    <div className="h-[500px] relative overflow-hidden flex items-center justify-center bg-black/20 group/video">
                                        {/* Ambient Background Glow */}
                                        <div className="absolute inset-0 opacity-30 blur-3xl scale-110">
                                            <video
                                                src={TeachingVideo}
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                                className="w-full h-full object-cover"
                                            />
                                        </div>

                                        {/* The "Phone" Frame */}
                                        <div className="relative z-10 w-[240px] aspect-[9/16] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 transform transition-transform duration-700 group-hover/video:scale-105 group-hover/video:rotate-1">
                                            <video
                                                src={TeachingVideo}
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                                className="w-full h-full object-cover"
                                            />
                                            {/* Screen Glare/Reflection */}
                                            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-0 group-hover/video:opacity-100 transition-opacity duration-500 pointer-events-none" />
                                        </div>
                                    </div>

                                    {/* Right: Content */}
                                    <div className="p-8 md:p-12 flex flex-col justify-center backdrop-blur-sm">
                                        <h3 className="text-3xl font-display text-white mb-6">{item.title}</h3>
                                        <p className="text-white/60 text-lg leading-relaxed mb-8">
                                            {item.description}
                                        </p>
                                        <div className="mt-auto pt-6 border-t border-white/10 flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/50">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                                            </div>
                                            <span className="font-mono text-sm text-white/40 uppercase tracking-widest">Giving Back</span>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                        </div>
                    ))}
                </div>

            </div>
        </section>
    );
}
